import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Alert,
  Stack
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Quiz as QuizIcon,
  School as TrainingIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

/**
 * Learner-facing list of exams that are published, not tied to a course,
 * under the system Standalone exam catalog category with include_in_standalone_catalog.
 */
const StandaloneExamCatalog = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exams, setExams] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/exams/standalone-catalog');
      setExams(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load standalone exams');
      setExams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byCategory = exams.reduce((acc, ex) => {
    const id = ex.category?.id || 'unknown';
    if (!acc[id]) acc[id] = { category: ex.category, exams: [] };
    acc[id].exams.push(ex);
    return acc;
  }, {});

  const groups = Object.values(byCategory);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/training/catalog')} variant="outlined" size="small">
          Catalog
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <QuizIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Standalone exams
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Knowledge checks not tied to a course outline. Categories opt in under Manage Training → Categories. Exams must be published with no sub-category or course.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Admins: in <strong>Manage Training → Exams</strong>, check <strong>Standalone exam catalog</strong> on the exam and publish. Exams appear here and in <strong>Browse Catalog</strong> without course enrollment. Use <strong>Start</strong> to take an exam.
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : exams.length === 0 ? (
        <Typography color="text.secondary">No standalone exams are available yet.</Typography>
      ) : (
        <Stack spacing={3}>
          {groups.map((g) => (
            <Box key={g.category?.id || 'x'}>
              <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrainingIcon fontSize="small" color="primary" />
                {g.category?.name || 'Category'}
              </Typography>
              {g.category?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {g.category.description}
                </Typography>
              )}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Exam</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Time limit</TableCell>
                      <TableCell align="right">Pass %</TableCell>
                      <TableCell align="right">Start</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.exams.map((ex) => (
                      <TableRow key={ex.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>{ex.name}</Typography>
                          {ex.description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {ex.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={ex.status || '—'} variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{ex.time_limit_minutes != null ? `${ex.time_limit_minutes} min` : '—'}</TableCell>
                        <TableCell align="right">{ex.pass_score_percent != null ? `${ex.pass_score_percent}%` : '—'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PlayIcon />}
                            onClick={() => navigate(`/training/exam/${ex.id}/take`)}
                          >
                            Start
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default StandaloneExamCatalog;
